import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  InjectionToken,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';

import { IconName } from '@vsolv/vectors-ui/icons';
import { ThemeColor } from '@vsolv/vectors-ui/theming';

export interface AlertConfig {
  /** Default: info-circle */
  icon?: IconName;
  /** Default: primary */
  iconTheme?: ThemeColor;

  /** Default: EMPTY */
  title?: string;
  /** Default: EMPTY */
  text?: string;

  /** Default: true */
  autoClose?: boolean;
  /** Default: 5000 */
  closeDelay?: number;
  /** Default: false */
  hideCloseButton?: boolean;
  /** Fill the whole alert with the iconTheme color instead of the plain white card. Default: false */
  filled?: boolean;
}

export const ALERT_CONFIG_TOKEN = new InjectionToken<AlertConfig>('ALERT_CONFIG_TOKEN');

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'vs-alert',
  templateUrl: './alert.component.html',
  standalone: false,
  styles: [
    `
      @keyframes vs-alert-progress {
        from {
          width: 0%;
        }
        to {
          width: 100%;
        }
      }

      .vs-alert-progress {
        animation-name: vs-alert-progress;
        animation-timing-function: linear;
        animation-fill-mode: forwards;
      }
    `,
  ],
})
export class AlertComponent implements OnInit, OnDestroy {
  @HostBinding('class') private get _class() {
    const display = 'block overflow-hidden';
    const border = 'border rounded-2xl shadow-lg';
    const size = 'min-w-[300px] max-w-md';
    const fill = this.config.filled
      ? `theme-${this.config.iconTheme} bg-themed-500 border-transparent`
      : 'bg-base border-gray-100';

    return `${display} ${border} ${size} ${fill}`;
  }

  @Output() readonly _delayCompleted = new EventEmitter();
  @Output() readonly _closeRequested = new EventEmitter();

  private _config: AlertConfig = {
    icon: 'info-circle',
    iconTheme: 'primary',

    autoClose: true,
    closeDelay: 5000,
    hideCloseButton: false,
  };

  set config(config: AlertConfig) {
    this._config = {
      icon: config.icon ?? 'info-circle',
      iconTheme: config.iconTheme ?? 'primary',

      title: config.title,
      text: config.text,

      autoClose: config.autoClose ?? true,
      closeDelay: config.closeDelay ?? 5000,
      hideCloseButton: config.hideCloseButton ?? false,
      filled: config.filled ?? false,
    };
  }
  get config() {
    return this._config;
  }

  _progressPaused = false;

  private timeLeft: number;
  private timeStarted?: number;

  private timer?: ReturnType<typeof setTimeout>;

  constructor(@Inject(ALERT_CONFIG_TOKEN) config: AlertConfig) {
    this.config = config;
    this.timeLeft = this.config.closeDelay ?? 5000;
  }

  ngOnInit() {
    this.initTimer();
  }

  ngOnDestroy() {
    this.disposeTimers();
  }

  close() {
    this.disposeTimers();
    this._closeRequested.emit();
  }

  @HostListener('mouseenter')
  _mouseenter() {
    this.disposeTimers();
    this.timeLeft -= Date.now() - (this.timeStarted ?? 0);
    this._progressPaused = true;
  }

  @HostListener('mouseleave')
  _mouseleave() {
    this._progressPaused = false;
    this.initTimer();
  }

  private disposeTimers() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
  }

  private initTimer() {
    if (this.config.autoClose) {
      this.timeStarted = Date.now();

      this.timer = setTimeout(() => {
        this.disposeTimers();
        this._delayCompleted.emit();
      }, this.timeLeft);
    }
  }
}
